import { z } from "zod";

export const APP_CATALOG_PAYMENT_MODES = ["LINK", "RECURRING"] as const;

export const APP_CATALOG_PROVIDERS = ["mercadopago", "flow"] as const;
export type AppCatalogProvider = (typeof APP_CATALOG_PROVIDERS)[number];

export const APP_CATALOG_DISCOUNT_TYPES = ["PERCENT", "FIXED"] as const;
export type AppCatalogDiscountType =
  (typeof APP_CATALOG_DISCOUNT_TYPES)[number];

export const APP_CATALOG_DISCOUNT_LABELS: Record<
  AppCatalogDiscountType,
  string
> = {
  PERCENT: "Porcentaje",
  FIXED: "Monto fijo",
};

export const APP_CATALOG_BADGES = [
  "PROMO",
  "OFERTA",
  "NUEVO",
  "POPULAR",
  "ULTIMA_OPORTUNIDAD",
] as const;
export type AppCatalogBadge = (typeof APP_CATALOG_BADGES)[number];

export const APP_CATALOG_BADGE_LABELS: Record<AppCatalogBadge, string> = {
  PROMO: "Promo",
  OFERTA: "Oferta",
  NUEVO: "Nuevo",
  POPULAR: "Popular",
  ULTIMA_OPORTUNIDAD: "Última oportunidad",
};

/**
 * Un color por etiqueta, para que el manager reconozca cuál eligió sin leer.
 * Las clases `bg-global-*` ya fijan el `border-color`, por eso va `border`.
 * NUEVO se apoya sólo en el fondo: no existe un `text-global-info` y crear uno
 * recoloreaba un chip que ya está en producción usando esa clase muerta.
 */
export const APP_CATALOG_BADGE_STYLES: Record<AppCatalogBadge, string> = {
  PROMO: "bg-global-accent-overlay text-global-accent border-global-primary",
  OFERTA: "bg-global-error text-global-error",
  NUEVO: "bg-global-info text-global-primary",
  POPULAR: "bg-global-success text-global-success",
  ULTIMA_OPORTUNIDAD: "bg-global-warning text-global-warning",
};

/** Los días viajan en inglés, como los guarda el backend; acá sólo se rotulan. */
export const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;
export type DayName = (typeof DAY_NAMES)[number];

export const DAY_INITIALS: Record<DayName, string> = {
  Monday: "L",
  Tuesday: "M",
  Wednesday: "X",
  Thursday: "J",
  Friday: "V",
  Saturday: "S",
  Sunday: "D",
};

export const DAY_LABELS: Record<DayName, string> = {
  Monday: "Lunes",
  Tuesday: "Martes",
  Wednesday: "Miércoles",
  Thursday: "Jueves",
  Friday: "Viernes",
  Saturday: "Sábado",
  Sunday: "Domingo",
};

export const APP_CATALOG_PERCENT_MAX = 90;
export const APP_CATALOG_PROMO_MESSAGE_MAX = 80;

export const appCatalogPlanSchema = z.object({
  publicId: z.string(),
  name: z.string(),
  price: z.number().nullable(),
  durationDays: z.number(),
  createsSubscription: z.boolean(),
  visible: z.boolean(),
  mode: z.enum(APP_CATALOG_PAYMENT_MODES),
  // Con default y no requerido: el endpoint ya está en producción sin este
  // campo, así que exigirlo tumbaba el tab entero si el frontend se desplegaba
  // antes que el backend. Se puede volver requerido cuando ambos estén arriba.
  provider: z.enum(APP_CATALOG_PROVIDERS).default("mercadopago"),
  requiresPlanPublicId: z.string().nullable(),
  // Mismo criterio que `provider`: con default para que el tab no se caiga si
  // el frontend se despliega antes que el backend.
  discountType: z.enum(APP_CATALOG_DISCOUNT_TYPES).nullable().default(null),
  discountValue: z.number().default(0),
  badge: z.enum(APP_CATALOG_BADGES).nullable().default(null),
  promoMessage: z.string().nullable().default(null),
  // Mismo criterio de default: el tab no se cae si el frontend sale antes.
  // Legacy: keys del enum viejo. Se lee pero no se usa ni se envía.
  requiredTags: z.array(z.string()).default([]),
  requiredTagPublicIds: z.array(z.string()).default([]),
  availableDays: z.array(z.enum(DAY_NAMES)).default([]),
  availableHours: z
    .object({ startHour: z.number(), endHour: z.number() })
    .nullable()
    .default(null),
});
export type AppCatalogPlan = z.infer<typeof appCatalogPlanSchema>;

export const appCatalogRequirablePlanSchema = z.object({
  publicId: z.string(),
  name: z.string(),
  price: z.number().nullable(),
});
export type AppCatalogRequirablePlan = z.infer<
  typeof appCatalogRequirablePlanSchema
>;

export const appCatalogResponseSchema = z.object({
  configuredProviders: z.array(z.enum(APP_CATALOG_PROVIDERS)).default([]),
  visibleCount: z.number(),
  plans: z.array(appCatalogPlanSchema),
  pagination: z.object({
    total: z.number(),
    page: z.number(),
    limit: z.number(),
    totalPages: z.number(),
  }),
  requirablePlans: z.array(appCatalogRequirablePlanSchema),
  /** Socios con cada etiqueta hoy; una etiqueta sin socios puede no venir. */
  tagCountsByPublicId: z.record(z.string(), z.number()).default({}),
});
export type AppCatalogResponse = z.infer<typeof appCatalogResponseSchema>;

export type AppCatalogPaymentMode = (typeof APP_CATALOG_PAYMENT_MODES)[number];

export interface AppCatalogFilters {
  visible?: boolean;
  mode?: AppCatalogPaymentMode;
  provider?: AppCatalogProvider;
}

export interface AppCatalogEntryInput {
  planPublicId: string;
  visible: boolean;
  mode: AppCatalogPaymentMode;
  provider: AppCatalogProvider;
  requiresPlanPublicId: string | null;
  discountType: AppCatalogDiscountType | null;
  discountValue: number;
  badge: AppCatalogBadge | null;
  promoMessage: string | null;
  availableDays: DayName[];
  availableHours: AppCatalogHours | null;
}

/** Rango `[startHour, endHour)`: 14–18 vende hasta las 17:59. */
export interface AppCatalogHours {
  startHour: number;
  endHour: number;
}

const WEEKDAY_BY_ABBREVIATION: Record<string, DayName> = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

/**
 * Espejo de `planIsWithinWindow` del backend: dice si el plan se está
 * ofreciendo en la app AHORA, leyendo el reloj del gimnasio y no el del
 * navegador (un manager en otro huso vería el aviso al revés).
 */
export const isWithinWindow = (
  entry: Pick<AppCatalogEntryInput, "availableDays" | "availableHours">,
  timezone: string,
): boolean => {
  const { availableDays, availableHours } = entry;
  if (availableDays.length === 0 && !availableHours) return true;

  let day: DayName | undefined;
  let hour: number;
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      weekday: "short",
      hour: "2-digit",
      hourCycle: "h23",
    }).formatToParts(new Date());
    const pick = (type: string) =>
      parts.find((part) => part.type === type)?.value ?? "";
    day = WEEKDAY_BY_ABBREVIATION[pick("weekday")];
    hour = Number(pick("hour"));
  } catch {
    // Una zona desconocida hace tirar a Intl: mejor no avisar nada que romper
    // la fila. Mismo criterio que el backend, que ahí ignora la restricción.
    return true;
  }
  if (!day || !Number.isFinite(hour)) return true;

  if (availableDays.length > 0 && !availableDays.includes(day)) return false;

  return (
    !availableHours ||
    (hour >= availableHours.startHour && hour < availableHours.endHour)
  );
};

export const hourLabel = (hour: number) =>
  `${String(hour).padStart(2, "0")}:00`;

/** `L M X J V · 14:00–17:59`; vacío cuando el plan no tiene restricción. */
export const windowSummary = (
  entry: Pick<AppCatalogEntryInput, "availableDays" | "availableHours">,
): string => {
  const days = DAY_NAMES.filter((day) => entry.availableDays.includes(day))
    .map((day) => DAY_INITIALS[day])
    .join(" ");
  // El fin es exclusivo: mostrar "14–18 h" hacía que el chip contradijera a la
  // ayuda del bloque, que ya dice "de 14:00 a 17:59".
  const hours = entry.availableHours
    ? `${hourLabel(entry.availableHours.startHour)}–${hourLabel(entry.availableHours.endHour - 1).replace(":00", ":59")}`
    : "";
  return [days, hours].filter(Boolean).join(" · ");
};

/** Descuento en pesos; espejo de `appCatalogDiscountAmount` del backend. */
export const appCatalogDiscountAmount = (
  planPrice: number,
  entry: Pick<AppCatalogEntryInput, "discountType" | "discountValue" | "mode">,
): number => {
  if (!entry.discountType || entry.mode === "RECURRING" || planPrice <= 0) {
    return 0;
  }

  if (entry.discountType === "PERCENT") {
    return Math.max(
      0,
      Math.min(
        Math.floor((planPrice * entry.discountValue) / 100),
        planPrice - 1,
      ),
    );
  }

  // Igual que el backend: un fijo que llega al precio no se aplica, en vez de
  // recortarse y dejar el plan en 1.
  return entry.discountValue >= planPrice ? 0 : entry.discountValue;
};
